import React from 'react';
import { ListItem, ListItemText, Box, Switch, Chip, IconButton, Tooltip } from '@mui/material';
import { withStyles } from 'tss-react/mui';
import ArrowCircleRightRoundedIcon from '@mui/icons-material/ArrowCircleRightRounded';
import TooltipHelpButton from '../../../../buttons/TooltipHelpButton/TooltipHelpButton';

// The navigation button mirrors TooltipHelpButton's geometry (zero padding, 5px lead-in,
// 18px icon) so the two helpers read as one inline pair right after the setting name,
// the same way tooltip buttons sit next to titles everywhere else in the editor.
const HELPER_ICON_FONT_SIZE = 18;
const HELPER_BUTTON_LEFT_MARGIN = 5;

const styles = theme => ({
    actionsContainer: {
        display: 'flex',
        alignItems: 'center'
    },
    newBadge: {
        fontSize: '0.65rem',
        fontWeight: 500,
        height: 18,
        marginRight: theme.spacing(1)
    },
    navigationButton: {
        padding: 0,
        marginLeft: HELPER_BUTTON_LEFT_MARGIN,
        color: theme.palette.text.secondary,
        '&:hover': {
            color: theme.palette.primary.main
        }
    },
    navigationIcon: {
        fontSize: HELPER_ICON_FONT_SIZE
    },
    disabledTitle: {
        color: theme.palette.text.disabled
    }
});

// Wraps the locked switch in the "why" tooltip. A disabled MUI Switch swallows pointer events,
// so the tooltip has to hang off the surrounding container rather than the switch itself.
const wrapWithDisabledTooltip = (disabled, tooltipText, content) => {
    if (!disabled || !tooltipText)
        return content;

    return (
        <Tooltip title={tooltipText} placement='top' arrow enterTouchDelay={0}>
            {content}
        </Tooltip>
    );
};

const FeatureListItem = ({ classes, feature, checked, disabled = false, showNewBadge, onToggle, onNavigate }) => {
    const actions = (
        <Box className={classes.actionsContainer}>
            {showNewBadge &&
                <Chip
                    label='NEW'
                    color='primary'
                    size='small'
                    className={classes.newBadge}
                />
            }
            <Switch
                checked={checked}
                disabled={disabled}
                onChange={(event) => onToggle(event.target.checked)}
                color='primary'
                inputProps={{ 'aria-label': `Toggle ${feature.title}` }}
            />
        </Box>
    );

    return (
        <ListItem id={`feature-list-item-${feature.id}`} aria-disabled={disabled}>
            <ListItemText className={disabled ? classes.disabledTitle : undefined}>
                {feature.title}
                {feature.tooltip && <TooltipHelpButton title={feature.tooltip} />}
                {onNavigate &&
                    <IconButton
                        className={classes.navigationButton}
                        onClick={onNavigate}
                        size='small'
                        aria-label={`Open ${feature.title} settings`}
                    >
                        <ArrowCircleRightRoundedIcon className={classes.navigationIcon} />
                    </IconButton>
                }
            </ListItemText>
            {wrapWithDisabledTooltip(disabled, feature.disabledTooltip, actions)}
        </ListItem>
    );
};

export default withStyles(FeatureListItem, styles);
